/**
 * WEB-2704 — тела действий «Дублировать экран» и «Дублировать блок»
 * хранилища редактора. Вынесены из фасада `editorSlice.ts` (храповик длины
 * фасада WEB-2552 — он только опускается); фасад оборачивает их
 * `withHistoryPayload`, так что одно действие = один шаг «Отменить».
 *
 * Копия получает свежие id на любой глубине (`./duplicate.ts`) и свободное
 * имя переменной ответа среди имён всей воронки (WEB-3326). «Логика» не
 * трогается: правила остаются на исходном экране. Неизвестный экран или блок —
 * то же состояние по ссылке (и без шага истории).
 */
import type { PayloadAction } from '@reduxjs/toolkit';
import type { EditorState } from './state';
import {
  cloneBlockWithFreshIds,
  cloneScreenWithFreshIds,
  funnelVariableNames,
} from './duplicate';

/**
 * Копия экрана встаёт сразу за исходным, имя = имя исходного + суффикс на
 * языке кабинета (его подставляет панель — редьюсер словаря не знает).
 */
export function duplicateScreenState(
  state: EditorState,
  action: PayloadAction<{ screenId: string; nameSuffix: string }>,
): EditorState {
  const { screenId, nameSuffix } = action.payload;
  const at = state.content.findIndex((s) => s.id === screenId);
  if (at < 0) return state;
  const source = state.content[at];
  const copy = cloneScreenWithFreshIds(
    source,
    `${source.name}${nameSuffix}`,
    funnelVariableNames(state.content),
  );
  return {
    ...state,
    content: [
      ...state.content.slice(0, at + 1),
      copy,
      ...state.content.slice(at + 1),
    ],
    selectedScreenId: copy.id,
    selectedBlockId: null,
    isDirty: true,
  };
}

/** Копия блока — на том же экране сразу под исходным, копия выбрана. */
export function duplicateBlockState(
  state: EditorState,
  action: PayloadAction<{ screenId: string; blockId: string }>,
): EditorState {
  const { screenId, blockId } = action.payload;
  const screen = state.content.find((s) => s.id === screenId);
  const at = screen?.blocks.findIndex((b) => b.id === blockId) ?? -1;
  if (!screen || at < 0) return state;
  const copy = cloneBlockWithFreshIds(
    screen.blocks[at],
    funnelVariableNames(state.content),
  );
  return {
    ...state,
    content: state.content.map((s) =>
      s.id === screenId
        ? {
            ...s,
            blocks: [
              ...s.blocks.slice(0, at + 1),
              copy,
              ...s.blocks.slice(at + 1),
            ],
          }
        : s,
    ),
    selectedScreenId: screenId,
    selectedBlockId: copy.id,
    isDirty: true,
  };
}
